import React, { ReactElement, useEffect, useRef, useCallback } from "react";
import TaskCard from "./day-plan-cards/TaskCard";
import { HourlyTaskCard, DailyTaskCard, CustomTaskCard, TaskStatus, TASK_STATUS, PlanData } from "../../../types/types";
import { TimeSegment } from "../../../utils/timeSegmentation";

interface StatusColumnProps {
  status: TaskStatus;
  label: string;
  hourlyBuckets?: Map<string, HourlyTaskCard[]>;
  dailyCards?: DailyTaskCard[];
  customBuckets?: Map<string, CustomTaskCard[]>;
  timeSegments: TimeSegment[];
  activeHours?: Set<number>;
  expandedIndex: number | null;
  onToggleExpand: (index: number) => void;
  onHourlyStatusChange?: (card: HourlyTaskCard, from: string, to: string) => void;
  onDailyStatusChange?: (card: DailyTaskCard, from: string, to: string) => void;
  onCustomStatusChange?: (card: CustomTaskCard, from: string, to: string) => void;
  getHourlyCardEditability?: (card: HourlyTaskCard) => { isEditable: boolean; updateHour: string };
  onViewBreakdown?: (planData: PlanData) => void;
}

const PRIORITY_ORDER: Record<string, number> = {
  "First": 0,
  "Later": 1,
  "Last": 2,
};

function StatusColumn({
  status,
  label,
  hourlyBuckets,
  dailyCards,
  customBuckets,
  timeSegments,
  activeHours,
  expandedIndex,
  onToggleExpand,
  onHourlyStatusChange,
  onDailyStatusChange,
  onCustomStatusChange,
  getHourlyCardEditability,
  onViewBreakdown,
}: StatusColumnProps) {
  
  const contentRef = useRef<HTMLDivElement>(null);
  const segmentRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const getHourlyCardKey = (card: HourlyTaskCard): string => {
    return `${card.parentPlanData.itemCode}-${card.hour}`;
  };

  const sortByPriorityAndName = useCallback(<T extends { parentPlanData: { dayPlanPrio: string; localName: string } }>(cards: T[]): T[] => {
    return [...cards].sort((a, b) => {
      const prioA = PRIORITY_ORDER[a.parentPlanData.dayPlanPrio] ?? 1;
      const prioB = PRIORITY_ORDER[b.parentPlanData.dayPlanPrio] ?? 1;
      
      if (prioA !== prioB) {
        return prioA - prioB;
      }
      
      return a.parentPlanData.localName.localeCompare(b.parentPlanData.localName);
    });
  }, []);

  useEffect(() => {
    if (status !== TASK_STATUS.TODO || timeSegments.length === 0) return;

    const segmentHours = timeSegments
      .map(s => parseInt(s.splitTime.split(":")[0], 10))
      .filter(h => !isNaN(h))
      .sort((a, b) => a - b);

    if (segmentHours.length === 0) return;

    const firstHour = segmentHours[0];
    const lastHour = segmentHours[segmentHours.length - 1];

    let firstNonEmptyHour: number | null = null;

    const beforeCards = hourlyBuckets?.get(`before-${firstHour}`) || [];
    if (beforeCards.length > 0) {
      return;
    }

    for (let h = firstHour; h < lastHour; h++) {
      const hourCards = hourlyBuckets?.get(`hour-${h}`) || [];
      if (hourCards.length > 0) {
        firstNonEmptyHour = h;
        break;
      }
    }

    if (firstNonEmptyHour !== null && segmentRefs.current.has(firstNonEmptyHour)) {
      const segmentElement = segmentRefs.current.get(firstNonEmptyHour);
      if (segmentElement && contentRef.current) {
        const containerTop = contentRef.current.offsetTop;
        const segmentTop = segmentElement.offsetTop;
        contentRef.current.scrollTop = segmentTop - containerTop;
      }
    }
  }, [status, hourlyBuckets, timeSegments]);

  const renderDailyContent = (): ReactElement | null => {
    if (!dailyCards || dailyCards.length === 0) return null;

    const sortedCards = sortByPriorityAndName(dailyCards);
    const elements: ReactElement[] = [];

    sortedCards.forEach((card, index) => {
      const updateHour = card.itemSalesPlan.plannedDayHour.substring(8, 10);
      elements.push(
        <TaskCard
          key={card.parentPlanData.itemCode}
          planData={card.parentPlanData}
          currentStatus={status}
          onDailyStatusChange={onDailyStatusChange}
          dailyCard={card}
          isExpanded={expandedIndex === index}
          onToggle={() => onToggleExpand(index)}
          isEditable={true}
          updateHour={updateHour}
          onViewBreakdown={onViewBreakdown}
        />
      );
    });

    return <>{elements}</>;
  };

  const renderNonSegmentedContent = (): ReactElement | null => {
    const allCards = hourlyBuckets?.get("all") || [];
    
    if (allCards.length === 0) return null;

    const sortedCards = sortByPriorityAndName(allCards);
    const elements: ReactElement[] = [];
    sortedCards.forEach((card, index) => {
      const { isEditable, updateHour } = getHourlyCardEditability ?  getHourlyCardEditability(card) : { isEditable: false, updateHour: "" };
      elements.push(
        <TaskCard
          key={getHourlyCardKey(card)}
          planData={card.parentPlanData}
          currentStatus={status}
          onHourlyStatusChange={onHourlyStatusChange}
          hourlyCard={card}
          isExpanded={expandedIndex === index}
          onToggle={() => onToggleExpand(index)}
          isEditable={isEditable}
          updateHour={updateHour}
          onViewBreakdown={onViewBreakdown}
        />
      );
    });

    return <>{elements}</>;
  };
  
  const renderHourlyViewContent = (): ReactElement | null => {
    const segmentHours = timeSegments
      .map(s => parseInt(s.splitTime.split(":")[0], 10))
      .filter(h => !isNaN(h))
      .sort((a, b) => a - b);

    if (segmentHours.length === 0) return null;

    const firstHour = segmentHours[0];
    const lastHour = segmentHours[segmentHours.length - 1];

    let globalIndex = 0;
    const elements: ReactElement[] = [];

    const beforeCards = sortByPriorityAndName(hourlyBuckets?.get(`before-${firstHour}`) || []);
    beforeCards.forEach((card) => {
      const { isEditable, updateHour } = getHourlyCardEditability ?  getHourlyCardEditability(card) : { isEditable: false, updateHour: "" };
      elements.push(
        <TaskCard
          key={getHourlyCardKey(card)}
          planData={card.parentPlanData}
          currentStatus={status}
          onHourlyStatusChange={onHourlyStatusChange}
          hourlyCard={card}
          isExpanded={expandedIndex === globalIndex}
          onToggle={() => onToggleExpand(globalIndex)}
          isEditable={isEditable}
          updateHour={updateHour}
          onViewBreakdown={onViewBreakdown}
        />
      );
      globalIndex++;
    });

    for (let h = firstHour; h < lastHour; h++) {
      // Skip hours with no cards in any status
      if (activeHours && !activeHours.has(h)) {
        continue;
      }

      elements.push(
        <div 
          key={`segment-${h}`} 
          className="time-segment-label"
          ref={(el) => {
            if (el) {
              segmentRefs.current.set(h, el);
            }
          }}
        >
          {`${h.toString().padStart(2, "0")}:00`}
        </div>
      );

      const hourCards = sortByPriorityAndName(hourlyBuckets?.get(`hour-${h}`) || []);
      hourCards.forEach((card) => {
        const currentIndex = globalIndex;
        const { isEditable, updateHour } = getHourlyCardEditability ?  getHourlyCardEditability(card) : { isEditable: false, updateHour: "" };
        elements.push(
          <TaskCard
            key={getHourlyCardKey(card)}
            planData={card.parentPlanData}
            currentStatus={status}
            onHourlyStatusChange={onHourlyStatusChange}
            hourlyCard={card}
            isExpanded={expandedIndex === currentIndex}
            onToggle={() => onToggleExpand(currentIndex)}
            isEditable={isEditable}
            updateHour={updateHour}
            onViewBreakdown={onViewBreakdown}
          />
        );
        globalIndex++;
      });
    }

    // Only show last segment label if that hour has cards
    if (!activeHours || activeHours.has(lastHour)) {
      elements.push(
        <div key={`segment-${lastHour}`} className="time-segment-label">
          {`${lastHour.toString().padStart(2, "0")}:00`}
        </div>
      );
    }

    const afterCards = sortByPriorityAndName(hourlyBuckets?.get(`after-${lastHour}`) || []);
    afterCards.forEach((card) => {
      const { isEditable, updateHour } = getHourlyCardEditability ?  getHourlyCardEditability(card) : { isEditable: false, updateHour: "" };
      elements.push(
        <TaskCard
          key={getHourlyCardKey(card)}
          planData={card.parentPlanData}
          currentStatus={status}
          onHourlyStatusChange={onHourlyStatusChange}
          hourlyCard={card}
          isExpanded={expandedIndex === globalIndex}
          onToggle={() => onToggleExpand(globalIndex)}
          isEditable={isEditable}
          updateHour={updateHour}
          onViewBreakdown={onViewBreakdown}
        />
      );
      globalIndex++;
    });

    return <>{elements}</>;
  };

  const renderCustomViewContent = (): ReactElement | null => {
    if (!customBuckets) return null;

    const segmentHours = timeSegments
      .map(s => parseInt(s.splitTime.split(":")[0], 10))
      .filter(h => !isNaN(h))
      .sort((a, b) => a - b);

    const numSlots = segmentHours.length + 1;
    let globalIndex = 0;
    const elements: ReactElement[] = [];

    for (let i = 0; i < numSlots; i++) {
      if (i > 0 && i - 1 < segmentHours.length) {
        const h = segmentHours[i - 1];
        elements.push(
          <div key={`custom-segment-${i}`} className="time-segment-label">
            {`${h.toString().padStart(2, "0")}:00`}
          </div>
        );
      }

      const slotCards = sortByPriorityAndName(customBuckets.get(`slot-${i}`) || []);
      slotCards.forEach((card) => {
        const currentIndex = globalIndex;
        const updateHour = card.itemSalesPlan.plannedDayHour.substring(8, 10);
        elements.push(
          <TaskCard
            key={`${card.parentPlanData.itemCode}-slot-${card.slotIndex}`}
            planData={card.parentPlanData}
            currentStatus={status}
            onCustomStatusChange={onCustomStatusChange}
            customCard={card}
            isExpanded={expandedIndex === currentIndex}
            onToggle={() => onToggleExpand(currentIndex)}
            isEditable={true}
            updateHour={updateHour}
            onViewBreakdown={onViewBreakdown}
          />
        );
        globalIndex++;
      });
    }

    return elements.length > 0 ? <>{elements}</> : null;
  };

  const renderNonSegmentedCustomContent = (): ReactElement | null => {
    if (!customBuckets) return null;
    const allCards = customBuckets.get("all") || [];
    if (allCards.length === 0) return null;

    const sortedCards = sortByPriorityAndName(allCards);
    const elements: ReactElement[] = [];
    sortedCards.forEach((card, index) => {
      const updateHour = card.itemSalesPlan.plannedDayHour.substring(8, 10);
      elements.push(
        <TaskCard
          key={`${card.parentPlanData.itemCode}-slot-${card.slotIndex}`}
          planData={card.parentPlanData}
          currentStatus={status}
          onCustomStatusChange={onCustomStatusChange}
          customCard={card}
          isExpanded={expandedIndex === index}
          onToggle={() => onToggleExpand(index)}
          isEditable={true}
          updateHour={updateHour}
          onViewBreakdown={onViewBreakdown}
        />
      );
    });

    return <>{elements}</>;
  };

  const getContainerLabelId = () => {
    switch (status) {
      case TASK_STATUS.TODO:
        return "todo-container-label";
      case TASK_STATUS.IN_PROGRESS:
        return "in-progress-container-label";
      case TASK_STATUS.DONE:
        return "done-container-label";
      default:
        return "";
    }
  };

  return (
    <div className="status-container">
      <div className="status-label-wrapper">
        <div id={getContainerLabelId()}>
          <label className="status-label">{label}</label>
        </div>
        <div className="status-container-content" ref={contentRef}>
          {dailyCards
            ? renderDailyContent()
            : customBuckets
              ? (timeSegments.length > 0 ? renderCustomViewContent() : renderNonSegmentedCustomContent())
              : timeSegments.length === 0
                ? renderNonSegmentedContent()
                : renderHourlyViewContent()}
        </div>
      </div>
    </div>
  );
}

export default StatusColumn;
